import React from 'react';
import { AlertTriangle, TrendingUp, UserCheck, Users } from 'lucide-react';
import { Panel, StatCard, EmptyState } from '../ui';

const TeamPerformanceDashboard = ({ teamMetrics = [] }) => {
  const activeMembers = teamMetrics.length;
  const highPerformers = teamMetrics.filter((m) => (m.taskCompletionRate || 0) >= 80).length;
  const needSupport = teamMetrics.filter((m) => (m.overdueTasks || 0) > 0).length;
  const avgEfficiency =
    teamMetrics.length > 0
      ? teamMetrics.reduce((sum, m) => sum + (m.efficiency || 0), 0) / teamMetrics.length
      : 0;

  return (
    <div className="space-y-6" data-testid="team-performance-dashboard">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard icon={Users} tone="blue" label="Active Members" value={activeMembers} />
        <StatCard icon={UserCheck} tone="green" label="High Performers" value={highPerformers} />
        <StatCard icon={AlertTriangle} tone="amber" label="Need Support" value={needSupport} />
        <StatCard
          icon={TrendingUp}
          tone="purple"
          label="Avg Efficiency"
          value={`${avgEfficiency.toFixed(1)}%`}
        />
      </div>

      <Panel>
        <h3 className="text-lg font-semibold mb-6 text-secondary-900">Individual Performance</h3>
        {teamMetrics.length === 0 ? (
          <EmptyState
            title="No Team Data Available"
            description="Team performance appears once members log time and receive tasks."
          />
        ) : (
          <div className="space-y-4">
            {teamMetrics.map((member) => (
              <div
                key={member.id}
                className="flex items-center justify-between p-4 bg-secondary-50 rounded-lg"
                data-testid={`team-member-row-${member.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-semibold text-primary-700">
                      {(member.full_name || member.username || 'U')
                        .split(' ')
                        .map((n) => n[0])
                        .join('')
                        .toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary-900">
                      {member.full_name || member.username || 'Unknown Member'}
                    </h4>
                    <p className="text-sm text-secondary-600">
                      {(member.productivity || 0).toFixed(1)}h •{' '}
                      {(member.taskCompletionRate || 0).toFixed(1)}% completion
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div
                    className={`text-sm font-semibold ${
                      (member.efficiency || 0) >= 80
                        ? 'text-green-600'
                        : (member.efficiency || 0) >= 60
                          ? 'text-yellow-600'
                          : 'text-red-600'
                    }`}
                  >
                    {(member.efficiency || 0).toFixed(1)}% efficiency
                  </div>
                  <div className="text-xs text-secondary-500">
                    {member.overdueTasks || 0} overdue • {member.totalTasks || 0} tasks
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
};

export default TeamPerformanceDashboard;
